/**
 * PicksControlsPanel - Game selection, market selection, ban list, and generate buttons.
 * Extracted from PicksView to reduce main component size.
 */

import { useScrollOverflow } from '@hooks/useScrollOverflow';
import type { GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import { DEFAULT_PRESET_ID } from '@parlay-picker/shared/configPreset';
import { hasPlayerProps, type League, leagueToLeagueKey } from '@parlay-picker/shared/league';
import type { LlmProvider } from '@parlay-picker/shared/llmProviders';
import type { ExternalApiHealth } from '@parlay-picker/shared/serviceHealth';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { usePresetsStore } from '@stores/presetsStore';
import { AlertTriangle, RefreshCw, SlidersHorizontal } from 'lucide-react';
import type React from 'react';
import { type ChangeEvent, useMemo, useRef, useState } from 'react';
import type { MarketOption } from '../constants';
import type { AvailablePlayer } from '../hooks/useBannedPlayers';
import type { GroupedGames } from '../utils';
import { BanListSection } from './BanListSection';
import { GameSelector } from './GameSelector';
import { GenerationHistoryDropdown } from './GenerationHistoryDropdown';
import { MarketSelector } from './MarketSelector';

// Provider types for admin dropdown
type ProviderSelection = LlmProvider | 'auto';

function QuickPresetChips({
  selectedLeague,
}: {
  selectedLeague: League;
}): React.ReactElement | null {
  const { presets, activePresetId, switchPreset } = usePresetsStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { canScrollRight } = useScrollOverflow(scrollRef);

  // Filter presets: Default (universal) + presets tagged for current league
  const selectedLeagueKey = leagueToLeagueKey(selectedLeague);
  const leaguePresets = useMemo(
    () => presets.filter((p) => p.id === DEFAULT_PRESET_ID || p.league === selectedLeagueKey),
    [presets, selectedLeagueKey],
  );

  // Only show if there are custom presets for this league (more than just Default)
  if (leaguePresets.length <= 1) return null;

  return (
    <div className="mb-4">
      <p className="text-xs text-theme-text-tertiary mb-1.5">Quick Presets</p>
      <div className="relative">
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-theme-bg to-transparent z-10 pointer-events-none rounded-r" />
        )}
        <div ref={scrollRef} className="flex gap-1.5 overflow-x-auto scrollbar-none">
          {leaguePresets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => switchPreset(preset.id)}
              className={`shrink-0 text-xs px-2 py-1 rounded-full transition-all ${
                activePresetId === preset.id
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill ring-1 ring-theme-primary/50'
                  : 'bg-theme-surface-hover text-theme-text-secondary hover:text-theme-text hover:bg-theme-border'
              }`}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface ModeQuota {
  used: number;
  limit: number;
  remaining: number;
}

export interface PicksControlsPanelProps {
  // League selection
  selectedLeague: League;
  // Game selection
  groupedGames: GroupedGames[];
  selectedGames: string[];
  onToggleGame: (gameId: string) => void;
  onSelectAllGames: () => void;
  onDeselectAllGames: () => void;
  totalGames: number;
  // Market selection
  markets: MarketOption[];
  selectedMarkets: string[];
  onToggleMarket: (marketKey: string) => void;

  // Ban list
  bannedPlayers: string[];
  onAddPlayer: (name: string) => void;
  onAddPlayers: (names: string[]) => void;
  onRemovePlayer: (name: string) => void;
  onRemovePlayers: (names: string[]) => void;
  availablePlayers: AvailablePlayer[];
  loadingPlayers: boolean;

  // Detailed analysis toggle
  showDetailedAnalysis: boolean;
  onShowDetailedAnalysisChange: (checked: boolean) => void;

  // Admin provider selection
  isAdmin: boolean;
  selectedProvider: ProviderSelection;
  onProviderChange: (provider: ProviderSelection) => void;

  // API health
  apiHealth: ExternalApiHealth;
  onRetryHealth: () => Promise<void>;

  // Generation
  isGenerating: boolean;
  recs: GeneratedParlay[];
  wasCached: boolean;
  onGenerate: (forceRefresh?: boolean) => Promise<void>;

  // Quota
  modeQuota: ModeQuota | null;
  analysisMode: string;
  onShowUpgrade: () => void;

  // History
  onLoadHistoryEntry: () => void;
}

export function PicksControlsPanel({
  selectedLeague,
  groupedGames,
  selectedGames,
  onToggleGame,
  onSelectAllGames,
  onDeselectAllGames,
  totalGames,
  markets,
  selectedMarkets,
  onToggleMarket,
  bannedPlayers,
  onAddPlayer,
  onAddPlayers,
  onRemovePlayer,
  onRemovePlayers,
  availablePlayers,
  loadingPlayers,
  showDetailedAnalysis,
  onShowDetailedAnalysisChange,
  isAdmin,
  selectedProvider,
  onProviderChange,
  apiHealth,
  onRetryHealth,
  isGenerating,
  recs,
  wasCached,
  onGenerate,
  modeQuota,
  analysisMode,
  onShowUpgrade,
  onLoadHistoryEntry,
}: PicksControlsPanelProps): React.ReactElement {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async (): Promise<void> => {
    setIsRetrying(true);
    try {
      await onRetryHealth();
    } finally {
      setIsRetrying(false);
    }
  };

  const leagueHasPlayerProps = hasPlayerProps(leagueToLeagueKey(selectedLeague));

  return (
    <>
      <GameSelector
        groupedGames={groupedGames}
        selectedGames={selectedGames}
        onToggleGame={onToggleGame}
        onSelectAll={onSelectAllGames}
        onDeselectAll={onDeselectAllGames}
        totalGames={totalGames}
      />

      {/* Market selector — shown for both NBA and NCAAB */}
      <MarketSelector
        markets={markets}
        selectedMarkets={selectedMarkets}
        onToggleMarket={onToggleMarket}
      />

      {/* Ban list — only for leagues with player props */}
      {leagueHasPlayerProps && (
        <BanListSection
          bannedPlayers={bannedPlayers}
          onAddPlayer={onAddPlayer}
          onAddPlayers={onAddPlayers}
          onRemovePlayer={onRemovePlayer}
          onRemovePlayers={onRemovePlayers}
          availablePlayers={availablePlayers}
          loadingPlayers={loadingPlayers}
          leagueKey={leagueToLeagueKey(selectedLeague)}
        />
      )}

      <QuickPresetChips selectedLeague={selectedLeague} />

      {/* Detailed analysis toggle — only for leagues with player props */}
      {leagueHasPlayerProps && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showDetailedAnalysis}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onShowDetailedAnalysisChange(e.target.checked)
              }
              className="rounded"
            />
            <span className="text-sm text-theme-text-secondary">
              Show detailed leg-by-leg analysis
            </span>
          </label>
          <div className="flex items-center gap-1.5">
            <GenerationHistoryDropdown onLoadEntry={onLoadHistoryEntry} />
            <ConfigShortcutButton />
          </div>
        </div>
      )}

      {/* Admin-only: LLM Provider Selection for A/B Testing */}
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-4 p-2 bg-purple-900/20 border border-purple-700/50 rounded-lg">
          <label className="text-sm text-purple-300 font-medium whitespace-nowrap">
            LLM Provider:
          </label>
          <select
            value={selectedProvider}
            onChange={(e) => onProviderChange(e.target.value as ProviderSelection)}
            disabled={isGenerating}
            className="flex-1 min-w-[140px] bg-theme-bg border border-theme-border rounded px-2 py-1 text-sm text-theme-text disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="auto">Auto (A/B Test)</option>
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="gemini">Google (Gemini)</option>
            <option value="deepseek">DeepSeek</option>
            <option value="openai">OpenAI (GPT)</option>
          </select>
          <span className="text-xs text-purple-400">Admin only</span>
        </div>
      )}

      {/* Odds API unavailable warning */}
      {!apiHealth.oddsApi && (
        <div className="bg-theme-danger/15 border border-theme-danger/30 rounded-lg p-3 mb-4 text-sm text-theme-danger">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Odds service is temporarily unavailable</span>
            </div>
            <button
              type="button"
              onClick={handleRetry}
              disabled={isRetrying}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-theme-danger/20 hover:bg-theme-danger/30 disabled:bg-theme-danger/10 rounded-md font-medium transition disabled:cursor-not-allowed"
              title="Check if odds service is back online"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
              {isRetrying ? 'Checking...' : 'Retry Now'}
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onGenerate(false)}
          disabled={
            isGenerating || !selectedGames.length || !selectedMarkets.length || !apiHealth.oddsApi
          }
          className="flex-1 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-border disabled:text-theme-text-tertiary px-4 py-3 rounded-lg font-semibold transition text-theme-on-primary-fill shadow-lg shadow-theme-primary/25 border-2 border-theme-primary-hover disabled:border-theme-border disabled:shadow-none"
          title={!apiHealth.oddsApi ? 'Odds service is temporarily unavailable' : undefined}
        >
          {isGenerating ? (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
              Fetching odds & generating picks...
            </span>
          ) : !apiHealth.oddsApi ? (
            'Odds Unavailable'
          ) : recs.length > 0 ? (
            'Regenerate Picks'
          ) : (
            `Generate Picks for ${selectedGames.length} Game${selectedGames.length !== 1 ? 's' : ''}`
          )}
        </button>
        {(recs.length > 0 || wasCached) && !isGenerating && apiHealth.oddsApi && (
          <button
            type="button"
            onClick={() => onGenerate(true)}
            className="bg-theme-bg hover:bg-theme-border px-4 py-3 rounded-lg font-medium transition text-sm text-theme-text"
            title="Bypass cache and get fresh picks"
          >
            Force New
          </button>
        )}
      </div>

      {/* Quota usage indicator (per-mode) */}
      {modeQuota && (
        <div className="flex items-center justify-between text-xs text-theme-text-tertiary mt-1.5 px-1">
          <span>
            {modeQuota.remaining > 0
              ? `${modeQuota.remaining} of ${modeQuota.limit} ${analysisMode} remaining today`
              : `No ${analysisMode} generations remaining today`}
          </span>
          {modeQuota.remaining <= 0 && (
            <button
              type="button"
              onClick={onShowUpgrade}
              className="text-theme-primary hover:text-theme-primary-hover transition"
            >
              Upgrade
            </button>
          )}
        </div>
      )}
    </>
  );
}

/** Gear icon for quick AI Config access — hidden on xl where HeaderShortcuts is visible */
function ConfigShortcutButton(): React.ReactElement {
  const { openConfigPanel } = useGlobalShortcutsStore();
  return (
    <button
      type="button"
      onClick={openConfigPanel}
      className="@7xl:hidden p-3 rounded-lg bg-theme-surface hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text transition"
      title="AI Config"
      aria-label="AI analysis settings"
    >
      <SlidersHorizontal className="h-5 w-5" />
    </button>
  );
}
